import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Avatar,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import api, { uploadsUrl } from '../../utils/api';
import AuthImage from '../../components/AuthImage';

function getPhotoUrl(displayPhoto, profilePicture) {
  const file = displayPhoto || profilePicture;
  if (!file) return null;
  return uploadsUrl(file);
}

function getPhotoFilename(displayPhoto, profilePicture) {
  const file = displayPhoto || profilePicture;
  if (!file || file.startsWith('http')) return null;
  return file.replace(/^\/+/, '');
}

// Avatar with image support - uses AuthImage for protected uploads, falls back to initials
function ExecutiveAvatar({ exec, size = 72, border }) {
  const photoUrl = getPhotoUrl(exec?.display_photo, exec?.profile_picture);
  const photoFilename = getPhotoFilename(exec?.display_photo, exec?.profile_picture);
  const initials = ((exec?.first_name?.[0] || '') + (exec?.last_name?.[0] || '')).toUpperCase() || '?';
  const avatarSx = {
    width: size,
    height: size,
    mx: 'auto',
    mb: 0.5,
    fontSize: size * 0.4,
    fontWeight: 600,
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    overflow: 'hidden',
    '& img': { objectFit: 'cover' },
    ...(border && { border: '3px solid', borderColor: 'primary.main', boxShadow: 2 }),
  };

  // Use AuthImage when we have a filename (works for both protected and static uploads)
  if (photoFilename) {
    return (
      <Avatar sx={avatarSx}>
        <AuthImage
          filename={photoFilename}
          alt={initials}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%',
            display: 'block',
          }}
        />
      </Avatar>
    );
  }

  // Fallback: use img src for full URLs, or initials only
  return (
    <Avatar
      src={photoUrl || undefined}
      imgProps={photoUrl ? { onError: (e) => { e.target.style.display = 'none'; } } : undefined}
      sx={avatarSx}
    >
      {initials}
    </Avatar>
  );
}

function ExecutiveTransitionModal({ open, onClose, electionName, outgoing, incoming, onSeen }) {
  const [phase, setPhase] = useState('outgoing');

  useEffect(() => {
    if (!open) return;
    setPhase('outgoing');
    const t = setTimeout(() => setPhase('incoming'), 3500);
    return () => clearTimeout(t);
  }, [open]);

  const handleContinue = async () => {
    await onSeen();
    onClose();
  };

  const flatOutgoing = Array.isArray(outgoing) ? outgoing : [];
  const flatIncoming = Array.isArray(incoming) ? incoming : [];

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="md"
      fullWidth
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: 'background.default',
          background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.04) 0%, rgba(46, 125, 50, 0.01) 100%)',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: 'text.primary' }}>
          New Executive Team
        </Typography>
        {electionName && (
          <Typography variant="h6" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
            {electionName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
        {phase === 'outgoing' && (
          <Fade in={phase === 'outgoing'} timeout={600}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                Thank you to our outgoing executives
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', maxWidth: 640 }}>
                {flatOutgoing.map((ex) => (
                  <Box key={ex.id} sx={{ textAlign: 'center', minWidth: 120 }}>
                    <ExecutiveAvatar exec={ex} size={88} />
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
                      {ex.first_name} {ex.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {ex.position_name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>
        )}
        {phase === 'incoming' && (
          <Fade in={phase === 'incoming'} timeout={800}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary.main" gutterBottom sx={{ mb: 3, fontWeight: 700 }}>
                Introducing your new executive team
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', maxWidth: 640 }}>
                {flatIncoming.map((ex) => (
                  <Box key={ex.id} sx={{ textAlign: 'center', minWidth: 120 }}>
                    <ExecutiveAvatar exec={ex} size={88} border />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, color: 'text.primary' }}>
                      {ex.first_name} {ex.last_name}
                    </Typography>
                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                      {ex.position_name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
        <Button variant="contained" size="large" onClick={handleContinue} color="primary">
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function MemberExecutives() {
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transitionData, setTransitionData] = useState(null);
  const [showTransition, setShowTransition] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    if (!token) {
      navigate('/member-portal/login');
      return;
    }
    api.defaults.headers.common['x-auth-token'] = token;
    fetchExecutives();
    fetchTransitionStatus();
  }, [navigate]);

  const fetchExecutives = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/member-portal/executives');
      setExecutives(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('memberToken');
        localStorage.removeItem('member');
        navigate('/member-portal/login');
      } else {
        setError(err.response?.data?.message || 'Failed to load executives');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTransitionStatus = async () => {
    try {
      const token = localStorage.getItem('memberToken');
      if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const res = await api.get('/member-portal/executives/transition-status');
      const data = res.data?.data || {};
      if (data.show_transition) {
        setTransitionData({
          election_id: data.election_id,
          election_name: data.election_name,
          outgoing: data.outgoing || [],
          incoming: data.incoming || [],
        });
        setShowTransition(true);
      }
    } catch (err) {
      console.warn('Transition status fetch failed:', err?.response?.status);
    }
  };

  const handleTransitionSeen = async () => {
    if (!transitionData?.election_id) return;
    try {
      await api.post('/member-portal/executives/transition-seen', { election_id: transitionData.election_id });
      setTransitionData(null);
      setShowTransition(false);
    } catch {
      setShowTransition(false);
    }
  };

  const formatTerm = (start, end) => {
    if (!start && !end) return null;
    const s = start ? new Date(start).toLocaleDateString(undefined, { year: 'numeric', month: 'short' }) : '';
    const e = end ? new Date(end).toLocaleDateString(undefined, { year: 'numeric', month: 'short' }) : 'Present';
    return s ? `${s} – ${e}` : e;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <ExecutiveTransitionModal
        open={showTransition && !!transitionData}
        onClose={() => setShowTransition(false)}
        electionName={transitionData?.election_name}
        outgoing={transitionData?.outgoing || []}
        incoming={transitionData?.incoming || []}
        onSeen={handleTransitionSeen}
      />

      <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {executives.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No executives are currently listed.
            </Typography>
          </Paper>
        ) : (
          executives.map((group) => (
            <Box key={group.position.id} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 700, fontSize: '1.1rem' }}>
                {group.position.name}
              </Typography>
              <Grid container spacing={2}>
                {group.executives.map((exec) => (
                  <Grid item xs={12} sm={6} key={exec.id}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <ExecutiveAvatar exec={exec} size={56} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '1rem' }}>
                            {exec.first_name} {exec.last_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.9rem' }}>
                            {exec.email}
                          </Typography>
                          {formatTerm(exec.term_start, exec.term_end) && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', mt: 0.25 }}>
                              Term: {formatTerm(exec.term_start, exec.term_end)}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))
        )}
      </Container>
    </Box>
  );
}
